import { ChangeDetectionStrategy, Component, computed, input, OnInit, output, Signal } from '@angular/core'
import { CertificateBaseComponent } from '@features/certificate/components/certificate-base/certificate-base.component'
import {
  CertCertificateHolderApiResponse,
  CertificateHolderApiResponse,
  CertificateHolderEmail
} from '@features/certificate-holder/models/certificate-holder.model'
import { ButtonDirective } from 'primeng/button'
import { Tooltip } from 'primeng/tooltip'
import {
  CertificateHolderEntityManagerComponent
} from '@features/certificate/components/certificate-holder-entity-manager/certificate-holder-entity-manager.component'
import {
  AddressDisplayActionsDirective,
  AddressSearchActionsDirective,
  HolderDisplayActionsDirective,
  HolderSearchActionsDirective
} from '@features/certificate/components/certificate-holder-entity-manager/certificate-holder-row-action.directive'
import {
  buildGroupedRows,
  buildSearchResultRows,
  CertificateHolderRow,
  createCertificateHolderSearch
} from '@features/certificate/utils/certificate-holder-search.util'

export type { CertificateHolderRow } from '@features/certificate/utils/certificate-holder-search.util'

@Component({
  selector: 'albert-certificate-certificate-holder',
  imports: [
    ButtonDirective,
    Tooltip,
    CertificateHolderEntityManagerComponent,
    HolderDisplayActionsDirective,
    AddressDisplayActionsDirective,
    HolderSearchActionsDirective,
    AddressSearchActionsDirective
  ],
  templateUrl: './certificate-certificate-holder.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CertificateCertificateHolderComponent extends CertificateBaseComponent implements OnInit {
  public readonly certificateHolders = input.required<Signal<CertCertificateHolderApiResponse[]>>()
  public readonly addHolderWithoutEmails = output<CertificateHolderApiResponse>()
  public readonly addSingleEmail = output<{ holder: CertificateHolderApiResponse; address: CertificateHolderEmail }>()
  public readonly removeHolder = output<number>()
  public readonly removeAddress = output<{ holderIndex: number; addressIndex: number }>()
  public readonly groupedRows = computed<CertificateHolderRow[]>(() => buildGroupedRows(this.certificateHolders()()))
  public readonly groupedRowsByHolder = computed(() => this.groupedRows().filter(row => row.type === this.certificateHolderRowType.HOLDER))
  public readonly searchResultRows = computed<CertificateHolderRow[]>(() =>
    buildSearchResultRows(this.searchResults(), this.certificateHolders()())
  )
  private readonly search = createCertificateHolderSearch()
  public readonly searchForm = this.search.searchForm
  public readonly searchQuery = this.search.searchQuery
  public readonly searchResults = this.search.searchResults
  public readonly isSearching = this.search.isSearching

  public ngOnInit(): void {
    this.search.init()
  }

  public onAddHolderWithoutEmails(holder: CertificateHolderApiResponse): void {
    this.addHolderWithoutEmails.emit(holder)
  }

  public onAddSingleEmail(holderIndex: number, addressIndex: number): void {
    const holder = this.searchResults()[holderIndex]!
    const address = (holder.recipientAddresses ?? [])[addressIndex]!
    this.addSingleEmail.emit({ holder, address })
  }

  public onRemoveHolder(holderIndex: number): void {
    this.removeHolder.emit(holderIndex)
  }

  public onRemoveAddress(holderIndex: number, addressIndex: number): void {
    this.removeAddress.emit({ holderIndex, addressIndex })
  }
}
